/**
 * The name to SHOW for an originator-authored entry — a generated PDF (`FileEntry`) or a
 * document-upload slot (`AdditionalDocumentEntry`) — in the given language (QA D-31).
 *
 * French uses `labelFr` when the originator authored one and falls back to the English `label`
 * otherwise, so a workflow nobody has translated yet reads exactly as before.
 *
 * DISPLAY ONLY. The English `label` is also the §14.1 DOCNAME BMO receives, in every language,
 * and the key NEW/REPLACE disposition matches on — anything BMO-facing (the upload's
 * `documentName`, the e-sign `description`) must keep reading `label` directly, never this.
 *
 * Which language to pass is the caller's decision and it differs by surface: an upload slot is
 * advisor UI text (FormShell's `language`), a generated PDF's name follows the client's chosen
 * PDF language (`pdfLanguage`), the same one that picks the French PDF file.
 */
export function localizedEntryLabel(
  entry: { label: string; labelFr?: string | null },
  language: 'en' | 'fr',
): string {
  if (language === 'fr') {
    const fr = entry.labelFr?.trim();
    if (fr) return fr;
  }
  return entry.label;
}

/**
 * The optional guidance to SHOW under a document slot's title, in the given language, or `''`
 * when the originator authored none.
 *
 * <p>
 * Same French-falls-back-to-English rule as {@link localizedEntryLabel}, and for the same reason:
 * a workflow whose descriptions nobody has translated yet should still tell a French advisor what
 * the document is, rather than showing them a blank where the English form has help.
 * </p>
 *
 * <p>
 * Returns the EMPTY STRING rather than null for "nothing authored", so the caller's test is a
 * plain truthiness check and a whitespace-only description — which renders as a gap on screen —
 * counts as nothing. Unlike the label, this reaches no one but the advisor: it is never a DOCNAME
 * and never travels to BMO.
 * </p>
 */
export function localizedEntryDescription(
  entry: { description?: string | null; descriptionFr?: string | null },
  language: 'en' | 'fr',
): string {
  if (language === 'fr') {
    const fr = entry.descriptionFr?.trim();
    if (fr) return fr;
  }
  return entry.description?.trim() ?? '';
}
